import * as React from 'react'
import PropTypes from 'prop-types'
// noinspection NpmUsedModulesInstalled
import { useLocation } from '@reach/router'
import Breadcrumbs, { getFinalBreadcrumbs } from './Breadcrumbs'
import HomeMenu from './HomeMenu'
import { useSelector, useDispatch } from 'react-redux'
import { applyCoupon, CHECKOUT_STATUS_SUCCESS } from '../../state/cart'
import { updateCheckoutField } from '../../state/checkout'

const Ticker = ({ ticker, openSundays, color = 'bg-green-800' }) => {
  let items = (ticker || '').split('\n').filter(Boolean) // Remove any empty lines
  if (openSundays) {
    items.push('WE ARE OPEN SUNDAYS THROUGH NEW YEARS EVE')
    items.push('WE ARE OPEN SUNDAYS 11:00AM to 5:00PM')
    items.push('CALL, CHAT OR VISIT OUR SHOWROOM!')
  }

  // Ensure there are at least 4 items
  if (items.length === 1) {
    items = [...items, ...items, ...items, ...items] // Duplicate to 4 times
  } else if (items.length === 2 || items.length === 3) {
    items = [...items, ...items] // Duplicate to make at least 4
  }

  const [currentIndex, setCurrentIndex] = React.useState(-1)
  React.useEffect(() => {
    setTimeout(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % items.length)
    }, 100)
  }, [items.length])

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % items.length)
    }, 3000) // Change line every 3 seconds

    return () => clearInterval(interval)
  }, [items.length])

  return (
    <div
      className={
        (currentIndex === -1 ? 'h-0 ' : 'h-10 ') +
        `overflow-hidden ${
          color || 'bg-green-800'
        } relative ease-in-out duration-500 border-b border-gray-300 `
      }
    >
      {items.map((item, index) => {
        let positionClass = 'top-12 opacity-0' // Default next position
        if (index === currentIndex) {
          positionClass = '-top-1 opacity-100' // Current line
        } else if (index === (currentIndex - 1 + items.length) % items.length) {
          positionClass = '-top-12 opacity-0' // Previous line
        }

        return (
          <div
            key={index}
            className={`absolute left-0 right-0 mx-auto text-center py-2 text-sm md:text-lg text-white font-semibold transition-all ease-in-out duration-500 ${positionClass}`}
          >
            {item}
          </div>
        )
      })}
    </div>
  )
}

Ticker.propTypes = {
  ticker: PropTypes.string,
  openSundays: PropTypes.bool,
  color: PropTypes.string,
}

const Header = ({
  breadcrumbs,
  title,
  canonical,
  navbarOpen,
  setNavbarOpen,
  h1 = '',
  children,
}) => {
  const { href: location, origin } = useLocation()
  const cleanBreadcrumbs = getFinalBreadcrumbs(breadcrumbs, title, location, origin, canonical)

  const cart = useSelector(state => state.cart)
  const checkout = useSelector(state => state.checkout)

  const dispatch = useDispatch()
  const [openSundays, setOpenSundays] = React.useState(true)

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const searchParams = new URLSearchParams(window.location.search)
    const couponCode = searchParams.get('coupon')
    if (couponCode) {
      const checkoutObj = { ...checkout, coupon_code: couponCode }
      dispatch(updateCheckoutField({ name: 'coupon_code', value: couponCode }))
      dispatch(applyCoupon({ cart, checkout: checkoutObj }))
    }
    // Open Sunday, whole December
    const today = new Date()
    setOpenSundays(today.getMonth() === 11)
  }, [])

  const tickerText = cart?.coupon_result?.ticker || ''
  const couponTicker = cart.coupon_status === CHECKOUT_STATUS_SUCCESS && tickerText.length > 0
  const hasTicker = openSundays || couponTicker

  return (
    <header itemScope itemType="https://schema.org/WPHeader" className="overscroll-x-none">
      <HomeMenu
        h1={h1}
        navbarOpen={navbarOpen}
        setNavbarOpen={setNavbarOpen}
        mainCategory={cleanBreadcrumbs?.length > 0 ? cleanBreadcrumbs[0] : null}
      />
      {hasTicker && (
        <Ticker
          ticker={tickerText}
          openSundays={openSundays}
          color={couponTicker ? 'bg-green-800' : 'bg-stone-800'}
        />
      )}
      {children}
      {breadcrumbs ? (
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          canonical={canonical}
          title={title}
          location={location}
          origin={origin}
        />
      ) : (
        ''
      )}
    </header>
  )
}

Header.propTypes = {
  h1: PropTypes.string,

  breadcrumbs: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  ]),
  shop: PropTypes.bool,
  canonical: PropTypes.string,
  title: PropTypes.string,
  navbarOpen: PropTypes.bool,
  setNavbarOpen: PropTypes.func,
  children: PropTypes.node,
}

Header.defaultProps = {
  breadcrumbs: false,
  shop: false,
  canonical: null,
  title: null,
}

export default Header
